/**
 * Brand geometry — the single source of truth for the platform P-mark.
 *
 * The mark reproduces the ratified concept sheet (bottom-left concept,
 * pixel-measured from the original at 1024px and scaled by 0.3977 into this
 * viewBox; original coordinates in comments as `[orig …]`):
 *
 *   - `p-stem` — a tall, dead-straight editorial bar (width 18) with two
 *     wedge flares in point symmetry: top-left and bottom-right, each a flat
 *     cut with a concave return into the body. Not calligraphic — the body
 *     edges are straight.
 *   - `p-bowl` — a detached SOLID sail: a near-vertical chord on the left,
 *     one big smooth arc on the right, and thin tips that hook back LEFT
 *     toward the stem. It floats right of the stem (the gap is ~2.3
 *     stem widths), its top aligned with the stem's top.
 *   - `p-dot` — the round full stop (operator addition, »P.«). It sits on
 *     the TEXT baseline. Its x-position is ELASTIC: at rest it sits under
 *     the bowl's belly; a long app name pushes it right (the dot terminates
 *     the name, never left of the bowl).
 *
 * The app name and the dot sit at the BOTTOM of the mark (operator revision
 * 2026-07-23): the shared baseline is the stem's bottom cut, the name is
 * 3× its original size and the dot 1.3× — the name tucks against the stem's
 * bottom-right flare and the ». « reads as a true full stop closing the
 * composition. Every coordinate lives here in ONE user-unit space (`VIEW`),
 * shared by the custom element, the animation timelines, and the standalone
 * SVG assets.
 */

/** The mark's coordinate system (user units). Width grows with the app name. */
export const VIEW = {
  /** Top of stem and bowl. [orig y=582] */
  capTop: 16,
  /**
   * The shared TEXT baseline — app name and dot sit on it, level with the
   * stem's bottom cut (the bottom of the mark; was 107.5 pre-revision).
   */
  baseline: 156,
  /** The stem's bottom cut — the baseline sits on it. [orig y=934] */
  tail: 156,
  /** ViewBox height (room below the baseline for Fraunces descenders). */
  height: 172,
  /** ViewBox width with NO app name (the bare »P.«). */
  minWidth: 142,
} as const;

/**
 * The stem: a straight bar [orig x 163..208 → 30..48, width 18], flat top
 * cut with the LEFT wedge flare [orig tip x=137 → 20], straight body edges,
 * and the point-symmetric RIGHT wedge flare at the bottom
 * [orig tip x=232 → 57.5] above a flat bottom cut.
 */
export const STEM_PATH = [
  "M 20 16", // top-left flare tip
  "L 48 16", // flat top cut
  "L 48 141.5", // straight right edge [orig flare starts y≈898]
  "C 48.5 146 51 150.5 57.5 155", // concave flare out to the bottom-right tip
  "L 30 156", // flat bottom cut
  "L 30 26.5", // straight left edge [orig flare ends y≈608]
  "C 23.5 18 21.5 17 20 16", // concave return up into the flare tip
  "Z",
].join(" ");

/**
 * The bowl — one solid sail. Outer edge: a single smooth arc from top tip
 * through the belly to the bottom tip. Inner edge: a near-vertical chord
 * whose ends curve back LEFT into the hooking tips.
 *
 * The whole sail is shifted LEFT by 18 units from its first cut — the
 * stem→closing-arc offset was tightened to 70% of its original value
 * (60 → 42 units, the closing tip landing at x=90 instead of x=108). Only
 * the bowl's x-position moved; its shape, the stem, the name slot, and the
 * dot's belly relationship are unchanged.
 */
export const BOWL_PATH = [
  "M 90 16.5", // top tip (hooks left, level with the stem top)
  "C 106 18 120.7 33.5 120.7 52", // outer arc to the belly
  "C 120.7 71.5 111 87 91.2 93.2", // outer arc down to the bottom tip
  "C 94.5 91 97 88 99.2 83.5", // tip hooks back up to the chord
  "L 99.2 27", // the chord — straight, vertical
  "C 96.5 24 93 20 90 16.5", // chord top curves out to the tip
  "Z",
].join(" ");

/** The full-stop dot (1.3× its original r=6 cut — operator revision). */
export const DOT = {
  r: 7.8,
  /** cy — the dot SITS on the text baseline. */
  cy: VIEW.baseline - 7.8,
  /** Leftmost allowed cx: under the bowl's belly (the bare »P.« position). */
  minCx: 121,
  /** Horizontal gap between the app name's end and the dot. */
  gapAfterName: 6,
} as const;

/**
 * The SLOT — where the app name sits: at the BOTTOM of the mark on the
 * shared baseline (the stem's bottom cut), tucked against the stem with
 * just enough clearance for the bottom-right wedge flare (tip x=57.5).
 * The name is 3× its original cut (operator revision; was fontSize 19 /
 * capHeight 13.5 on a mid-height baseline) — cap ≈ 29% of stem height.
 */
export const SLOT = {
  x: 64,
  baseline: VIEW.baseline,
  capHeight: 40.5,
  /** Font size that yields ~capHeight for Fraunces (cap ≈ 0.72 em). */
  fontSize: 57,
} as const;

/**
 * Named anchor points — first-class animation handles. Rendered as
 * zero-size `.anchor-*` nodes so tooling can query real coordinates.
 */
export const ANCHORS = {
  /** Baseline at the stem — where the composition starts. */
  baselineStart: { x: 30, y: VIEW.baseline },
  /** Where the app name begins (slot origin). */
  slotStart: { x: SLOT.x, y: SLOT.baseline },
  /** The bowl's top tip — the hinge the bowl swings from. */
  bowlHinge: { x: 90, y: 16.5 },
  /** The bowl's bottom tip — hooks toward the name below. */
  bowlTip: { x: 91.2, y: 93.2 },
  /** The dot's home with no app name (»P.«). */
  dotHome: { x: DOT.minCx, y: DOT.cy },
} as const;

export interface Point {
  x: number;
  y: number;
}

/** Dot cx for a given app-name width (0 = no name → the bare »P.«). */
export function dotCx(nameWidth: number): number {
  if (nameWidth <= 0) return DOT.minCx;
  return Math.max(DOT.minCx, SLOT.x + nameWidth + DOT.gapAfterName);
}

/** ViewBox width for a given app-name width. */
export function viewWidth(nameWidth: number): number {
  return Math.max(VIEW.minWidth, Math.ceil(dotCx(nameWidth) + DOT.r + 14));
}

/**
 * Fallback advance-width estimate (user units) for environments without
 * SVG text measurement (happy-dom, SSR). Tuned for Fraunces at SLOT.fontSize;
 * the element re-measures with `getComputedTextLength()` in real browsers.
 */
export function estimateNameWidth(name: string): number {
  const em = SLOT.fontSize;
  let width = 0;
  for (const ch of name) {
    if (/[A-Z]/.test(ch)) width += 0.72 * em;
    else if (/[mw]/.test(ch)) width += 0.86 * em;
    else if (/[ilíìj.,' ]/.test(ch)) width += 0.28 * em;
    else if (/[ftr]/.test(ch)) width += 0.38 * em;
    else width += 0.53 * em;
  }
  return width;
}
